import { useEffect, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import SlotModal from '@/components/PickupSlots/SlotModal';
import {
  useFetchPickupSlots,
  useFetchUser,
  useRespondPickupSlot,
  useUpdatePickupTimeSlot,
  useUpdateSlotPreference,
} from '@/hooksApi';
import useChangeSlotStore from '@/stores/useChangeSlotStore';
import type { MerchantSetting, PreferredTiming } from '@/types';

// Singleton "change pickup time slot" modal — mounted ONCE at the pickups
// page level and driven by useChangeSlotStore. It must live OUTSIDE the
// table row/dropdown tree: mounting it per-row froze the AntD zoom-appear
// animation (pointer-events:none stuck on .ant-modal) and clicks fell
// through to the row underneath (QA HIGH bug).
//
// mode 'edit' calls the plain single-pickup edit endpoint (row dropdown
// action + tomorrow-banner link) and renders the shared modal in its
// 'preference' visual (all 5 slots, tomorrow notes, closable). mode
// 'respond' calls the rollover-response endpoint with choice='change'
// (rollover "Change same-day slot") and renders the shared modal in its
// 'same-day-only' visual (all 5 slots shown, next-day ones disabled instead
// of hidden).
//
// v2-task-P2: edit mode ALSO renders the T9 auto-select toggle. Important —
// `auto_select_next_slot` is a STANDING merchant preference (governs future
// ships), NOT a per-pickup attribute, so this is two independent writes on
// Confirm: the per-pickup slot PATCH (always) and, only if the toggle value
// changed from its seeded value, a write to the standing preference via
// useUpdateSlotPreference — sent with the merchant's CURRENT preferred slot
// code (unchanged; only the toggle is being updated here). respond mode
// never touches the preference at all.
//
// v2-task-P3: edit mode ALSO renders the T10-FE recurring-pickup toggle,
// for consistency with the main preference modal — same standing-preference
// treatment as auto-select above (seeded from setting.recurring_pickup_enabled,
// edit-mode-only, gated on hasPreferredSlot). The preference write on
// Confirm now fires if EITHER toggle changed, carrying BOTH fields.
const ChangeSlotModal = () => {
  const { t } = useTranslation();
  const { isOpen, params, closeChangeSlot } = useChangeSlotStore();
  const [selectedCode, setSelectedCode] = useState<string | undefined>(undefined);
  const [autoSelectEnabled, setAutoSelectEnabled] = useState(false);
  const [recurringEnabled, setRecurringEnabled] = useState(false);

  const { data, isLoading } = useFetchPickupSlots(isOpen);
  const { data: user } = useFetchUser();
  const { mutate: updateSlot, isLoading: isUpdating } = useUpdatePickupTimeSlot();
  const { mutate: respondSlot, isLoading: isResponding } = useRespondPickupSlot();
  const { mutate: updatePreference, isLoading: isUpdatingPreference } = useUpdateSlotPreference();

  const setting = user?.object?.setting as MerchantSetting | undefined;
  const preferredSlotCode = setting?.pickup_time_slot_code ?? undefined;
  const hasPreferredSlot = !!preferredSlotCode;
  const seededAutoSelect = !!setting?.auto_select_next_slot;
  const seededRecurring = !!setting?.recurring_pickup_enabled;

  useEffect(() => {
    setSelectedCode(isOpen ? (params?.currentCode ?? undefined) : undefined);
    setAutoSelectEnabled(isOpen ? seededAutoSelect : false);
    setRecurringEnabled(isOpen ? seededRecurring : false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, params?.currentCode, seededAutoSelect, seededRecurring]);

  const isSaving = isUpdating || isResponding || isUpdatingPreference;
  const isRespond = (params?.mode ?? 'edit') === 'respond';

  const handleConfirm = (code?: string, preferredTiming?: PreferredTiming | null) => {
    const finalCode = code ?? selectedCode;
    if (!finalCode || !params) return;

    if (isRespond) {
      respondSlot(
        { uid: params.uid, choice: 'change', timeSlotCode: finalCode, preferredTiming },
        {
          onSuccess: () => {
            message.success(String(t('operation')));
            closeChangeSlot();
          },
        },
      );
      return;
    }

    // The applied slot's human label, for the relocation warning copy.
    const appliedSlotLabel =
      (data?.slots ?? []).find((slot) => slot.code === finalCode)?.label ?? finalCode;

    updateSlot(
      { uid: params.uid, timeSlotCode: finalCode, preferredTiming },
      {
        // Routed-pickup relocation: when this edit moved an ALREADY-routed
        // pickup onto the new slot the backend flags `relocated_from_route` —
        // warn the merchant (applied slot + pickup agent will be re-assigned)
        // instead of the plain success toast.
        onSuccess: (result) => {
          if (result?.relocated_from_route) {
            message.warning(String(t('slot_edit_relocated', { slot: appliedSlotLabel })));
          } else {
            message.success(String(t('operation')));
          }
          closeChangeSlot();
        },
      },
    );

    if (
      hasPreferredSlot &&
      (autoSelectEnabled !== seededAutoSelect || recurringEnabled !== seededRecurring)
    ) {
      updatePreference({
        timeSlotCode: preferredSlotCode,
        autoSelectNextSlot: autoSelectEnabled,
        recurringPickupEnabled: recurringEnabled,
      });
    }
  };

  return (
    <SlotModal
      open={isOpen}
      mode={isRespond ? 'same-day-only' : 'preference'}
      title={String(t(isRespond ? 'slot_modal_samedayonly_title' : 'change_time_slot'))}
      subtitle={String(
        t(isRespond ? 'slot_modal_subtitle_samedayonly' : 'slot_modal_subtitle_preference'),
      )}
      slots={data?.slots ?? []}
      isLoading={isLoading}
      selectedCode={selectedCode}
      onChangeSelected={setSelectedCode}
      onConfirm={handleConfirm}
      onCancel={closeChangeSlot}
      // §6.2 — EDIT mode re-picking the SAME slot pre-populates the pickup's
      // currently-saved preferred timing (threaded via the store); respond mode
      // has none (defaults to Anytime).
      currentSlotCode={params?.currentCode ?? undefined}
      currentPref={isRespond ? null : (params?.currentPreferredTiming ?? null)}
      isSaving={isSaving}
      autoSelectEnabled={autoSelectEnabled}
      onChangeAutoSelect={isRespond ? undefined : setAutoSelectEnabled}
      autoSelectDisabled={!hasPreferredSlot}
      recurringEnabled={recurringEnabled}
      onChangeRecurring={isRespond ? undefined : setRecurringEnabled}
      recurringDisabled={!hasPreferredSlot}
    />
  );
};

export default ChangeSlotModal;
